import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ClipboardList,
  LogOut,
  User,
  ChevronDown,
  Activity,
  Loader2,
  CheckCircle2,
  Clock,
  FileText,
  LayoutGrid,
  BarChart3,
  Target,
  TrendingUp,
  Download,
  Eye,
  Users,
  PieChart
} from 'lucide-react';
import { authAPI, surveysAPI } from '../services/api';

const SponsorDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [surveysData, setSurveysData] = useState({ surveys: [], total: 0, completed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyAnalytics, setSurveyAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const isAgency = user?.role === 'agency';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      // Redirect non-sponsor/agency to appropriate dashboard
      if (userData.role === 'administrator') {
        navigate('/dashboard');
        return;
      }
      if (userData.role === 'doctor') {
        navigate('/doctor');
        return;
      }
      
      const surveys = await authAPI.getMySurveys();
      setSurveysData(surveys);
      
      // Auto-select first survey
      if (surveys.surveys.length > 0) {
        loadSurveyAnalytics(surveys.surveys[0]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) navigate('/auth');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSurveyAnalytics = async (survey) => {
    setSelectedSurvey(survey);
    setLoadingAnalytics(true);
    try {
      const analytics = await surveysAPI.getAnalytics(survey.id);
      setSurveyAnalytics(analytics);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleExportReport = async (surveyId) => {
    try {
      const blob = await surveysAPI.exportReport(surveyId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const survey = surveysData.surveys.find(s => s.id === surveyId);
      link.setAttribute('download', `survey_report_${survey?.title?.replace(/\s+/g, '_') || surveyId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to export report');
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/auth');
  };

  const getTypeColor = (type) => {
    const colors = {
      kap: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      mrs: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      patient: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      hcp: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
    };
    return colors[type] || 'bg-slate-500/10 text-slate-400';
  };

  const getTypeName = (type) => {
    const names = { kap: 'KAP Survey', mrs: 'Market Research', patient: 'Patient Survey', hcp: 'HCP Feedback' };
    return names[type] || type;
  };

  const getRoleName = (role) => {
    const names = { sponsor: 'Sponsor', agency: 'Agency' };
    return names[role] || role;
  };

  const [activeView, setActiveView] = useState('dashboard'); // dashboard, surveys, analytics

  const completedCount = surveysData.surveys.filter(s => s.completed).length;
  const pendingCount = surveysData.total - completedCount;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 z-40 hidden lg:flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <button 
            onClick={() => setActiveView('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeView === 'dashboard' ? 'text-white bg-white/10 font-medium' : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <LayoutGrid className="w-5 h-5" />
            Dashboard
          </button>
          <button 
            onClick={() => setActiveView('surveys')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeView === 'surveys' ? 'text-white bg-white/10 font-medium' : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <ClipboardList className="w-5 h-5" />
            My Studies
            {surveysData.total > 0 && (
              <Badge className="ml-auto bg-purple-500/20 text-purple-400 text-xs">{surveysData.total}</Badge>
            )}
          </button>
          <button 
            onClick={() => setActiveView('analytics')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeView === 'analytics' ? 'text-white bg-white/10 font-medium' : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            Analytics
          </button>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-400 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate capitalize">{getRoleName(user?.role)}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300">
                <User className="w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}>
                <LogOut className="w-4 h-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 p-4 sm:p-6">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400">
                {user?.name?.charAt(0) || 'U'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300">
                <User className="w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}>
                <LogOut className="w-4 h-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Welcome Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
                Welcome, {user?.name}
              </h1>
              <p className="text-slate-400 mt-1 text-sm sm:text-base">View-only access to assigned studies</p>
            </div>
            <Badge className="w-fit bg-gradient-to-r from-purple-500/10 to-pink-400/10 text-purple-400 border-purple-500/20">
              <Eye className="w-3 h-3 mr-1" /> {getRoleName(user?.role)} - View Only
            </Badge>
          </div>
        </div>

        {/* Conditional Content Based on activeView */}
        {activeView === 'dashboard' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-slate-400">Total Studies</p>
                      <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{surveysData.total}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-400/20 flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-slate-400">Completed</p>
                      <p className="text-2xl sm:text-3xl font-bold text-emerald-400 mt-1">{completedCount}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-slate-400">In Progress</p>
                      <p className="text-2xl sm:text-3xl font-bold text-amber-400 mt-1">{pendingCount}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-slate-400">Total Responses</p>
                      <p className="text-2xl sm:text-3xl font-bold text-purple-400 mt-1">
                        {surveyAnalytics?.total_responses || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Studies List */}
              <Card className="bg-slate-900 border-slate-800 lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-lg">Assigned Studies</CardTitle>
                  <CardDescription className="text-slate-400">Select a study to view analytics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
                  {surveysData.surveys.length === 0 ? (
                    <div className="text-center py-8">
                      <ClipboardList className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">No studies assigned yet</p>
                    </div>
                  ) : (
                    surveysData.surveys.map((survey) => (
                      <div
                        key={survey.id}
                        onClick={() => loadSurveyAnalytics(survey)}
                        className={`p-3 sm:p-4 rounded-xl cursor-pointer transition-all ${
                          selectedSurvey?.id === survey.id
                            ? 'bg-purple-500/10 border border-purple-500/30'
                            : 'bg-slate-800/50 border border-transparent hover:border-slate-700'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white text-sm truncate">{survey.title}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="outline" className={`${getTypeColor(survey.type)} text-[10px]`}>
                                {getTypeName(survey.type)}
                              </Badge>
                              <span className="text-xs text-slate-500">{survey.questions_count} Q</span>
                            </div>
                          </div>
                          {isAgency && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportReport(survey.id);
                              }}
                              className="text-slate-400 hover:text-white h-8 w-8 p-0"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Analytics Panel */}
              <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-cyan-400" />
                        {selectedSurvey?.title || 'Select a Study'}
                      </CardTitle>
                      {selectedSurvey && (
                        <CardDescription className="text-slate-400">Detailed analytics and responses</CardDescription>
                      )}
                    </div>
                    {selectedSurvey && isAgency && (
                      <Button
                        onClick={() => handleExportReport(selectedSurvey.id)}
                        className="bg-gradient-to-r from-purple-500 to-pink-400 hover:from-purple-600 hover:to-pink-500 w-full sm:w-auto"
                      >
                        <Download className="w-4 h-4 mr-2" /> Export Excel
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingAnalytics ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                    </div>
                  ) : !selectedSurvey ? (
                    <div className="text-center py-12">
                      <PieChart className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                      <p className="text-slate-500">Select a study to view analytics</p>
                    </div>
                  ) : surveyAnalytics ? (
                    <div className="space-y-6">
                      {/* Progress Overview */}
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-slate-400">Completion Progress</span>
                          <span className="text-sm font-medium text-white">{surveyAnalytics.completion_rate}%</span>
                        </div>
                        <Progress value={surveyAnalytics.completion_rate} className="h-3 bg-slate-700" />
                        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                          <span>{surveyAnalytics.total_responses} responses</span>
                          <span>Target: {surveyAnalytics.target_responses}</span>
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4 text-center">
                          <p className="text-2xl sm:text-3xl font-bold text-cyan-400">{surveyAnalytics.total_responses}</p>
                          <p className="text-xs text-slate-500 mt-1">Total Responses</p>
                        </div>
                        <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4 text-center">
                          <p className="text-2xl sm:text-3xl font-bold text-emerald-400">{surveyAnalytics.assigned_users_count}</p>
                          <p className="text-xs text-slate-500 mt-1">Assigned Doctors</p>
                        </div>
                        <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4 text-center col-span-2 sm:col-span-1">
                          <p className="text-2xl sm:text-3xl font-bold text-amber-400">{surveyAnalytics.questions_count}</p>
                          <p className="text-xs text-slate-500 mt-1">Questions</p>
                        </div>
                      </div>

                      {/* Users by Role */}
                      {Object.keys(surveyAnalytics.users_by_role).length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-white mb-3">Respondents by Role</h4>
                          <div className="space-y-2">
                            {Object.entries(surveyAnalytics.users_by_role).map(([role, count]) => {
                              const percentage = surveyAnalytics.assigned_users_count > 0 
                                ? Math.round((count / surveyAnalytics.assigned_users_count) * 100) 
                                : 0;
                              return (
                                <div key={role} className="flex items-center gap-3">
                                  <span className="text-xs text-slate-400 capitalize w-16">{role}</span>
                                  <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-purple-500 to-cyan-400 rounded-full"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-slate-300 w-12 text-right">{count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Questions List */}
                      {surveyAnalytics.question_stats.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-white mb-3">Questions</h4>
                          <div className="space-y-2 max-h-[200px] overflow-y-auto">
                            {surveyAnalytics.question_stats.map((q, i) => (
                              <div key={q.id || i} className="flex items-center gap-3 p-2 bg-slate-800/30 rounded-lg">
                                <span className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-300">
                                  {i + 1}
                                </span>
                                <span className="flex-1 text-sm text-slate-300 truncate">{q.text || 'Question'}</span>
                                <Badge className="bg-slate-700 text-slate-300 text-[10px]">{q.type}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* My Studies View */}
        {activeView === 'surveys' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">My Studies</h2>
              <Badge className="bg-purple-500/20 text-purple-400">{surveysData.total} Total</Badge>
            </div>
            
            {surveysData.surveys.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-12 text-center">
                  <ClipboardList className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-400">No studies assigned yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {surveysData.surveys.map((survey) => (
                  <Card 
                    key={survey.id} 
                    className="bg-slate-900 border-slate-800 hover:border-purple-500/30 transition-all cursor-pointer"
                    onClick={() => {
                      setSelectedSurvey(survey);
                      loadSurveyAnalytics(survey);
                      setActiveView('analytics');
                    }}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{survey.title}</h3>
                          <p className="text-sm text-slate-400 mt-1 line-clamp-2">{survey.description || 'No description'}</p>
                        </div>
                        <Badge variant="outline" className={`${getTypeColor(survey.type)} text-xs flex-shrink-0`}>
                          {getTypeName(survey.type)}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4 text-slate-400">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {survey.questions_count} Q
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {survey.responses || 0}
                          </span>
                        </div>
                        <Badge className={survey.completed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                          {survey.completed ? 'Completed' : 'Active'}
                        </Badge>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between">
                        {isAgency && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportReport(survey.id);
                            }}
                            className="text-slate-400 hover:text-white gap-1"
                          >
                            <Download className="w-4 h-4" /> Export
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-purple-400 hover:text-purple-300 gap-1"
                        >
                          <Eye className="w-4 h-4" /> View Analytics
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analytics View */}
        {activeView === 'analytics' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Analytics</h2>
                <p className="text-slate-400 text-sm mt-1">View detailed statistics for your studies</p>
              </div>
              {selectedSurvey && isAgency && (
                <Button
                  onClick={() => handleExportReport(selectedSurvey.id)}
                  className="bg-gradient-to-r from-purple-500 to-pink-400 hover:from-purple-600 hover:to-pink-500"
                >
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
              {/* Survey Selector */}
              <Card className="bg-slate-900 border-slate-800 lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base">Select Study</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {surveysData.surveys.map((survey) => (
                    <div
                      key={survey.id}
                      onClick={() => loadSurveyAnalytics(survey)}
                      className={`p-3 rounded-xl cursor-pointer transition-all ${
                        selectedSurvey?.id === survey.id
                          ? 'bg-purple-500/10 border border-purple-500/30'
                          : 'bg-slate-800/50 border border-transparent hover:border-slate-700'
                      }`}
                    >
                      <p className="font-medium text-white text-sm truncate">{survey.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`${getTypeColor(survey.type)} text-[10px]`}>
                          {getTypeName(survey.type)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Analytics Display */}
              <div className="lg:col-span-3">
                {loadingAnalytics ? (
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-12 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                    </CardContent>
                  </Card>
                ) : !selectedSurvey || !surveyAnalytics ? (
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-12 text-center">
                      <PieChart className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                      <p className="text-slate-400">Select a study to view analytics</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {/* Stats Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-cyan-400">{surveyAnalytics.total_responses}</p>
                          <p className="text-xs text-slate-500 mt-1">Responses</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-emerald-400">{surveyAnalytics.completion_rate}%</p>
                          <p className="text-xs text-slate-500 mt-1">Completion</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-amber-400">{surveyAnalytics.assigned_users_count}</p>
                          <p className="text-xs text-slate-500 mt-1">Assigned</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-purple-400">{surveyAnalytics.questions_count}</p>
                          <p className="text-xs text-slate-500 mt-1">Questions</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Progress Card */}
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-white">Progress to Target</span>
                          <span className="text-sm text-slate-400">{surveyAnalytics.total_responses} / {surveyAnalytics.target_responses}</span>
                        </div>
                        <Progress value={surveyAnalytics.completion_rate} className="h-4 bg-slate-700" />
                      </CardContent>
                    </Card>

                    {/* Questions Overview */}
                    <Card className="bg-slate-900 border-slate-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white text-base">Questions Overview</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
                        {surveyAnalytics.question_stats.map((q, i) => (
                          <div key={q.id || i} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                            <span className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center text-sm text-cyan-400 font-semibold">
                              {i + 1}
                            </span>
                            <span className="flex-1 text-sm text-slate-300">{q.text || 'Question'}</span>
                            <Badge className="bg-slate-700 text-slate-300">{q.type}</Badge>
                            {q.required && <Badge className="bg-red-500/20 text-red-400">Required</Badge>}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorDashboardPage;
